/**
 * FlightTicketVerificationModal.js
 *
 * HR split-screen modal for verifying flight tickets submitted with Home Leave / Urgent Leave.
 *
 * Left panel  — document preview (PDF iframe or zoomable image with zoom/rotate controls)
 * Right panel — confirmed flight details (pre-filled from LeaveApplication), approve / reject,
 *               and HR date-amendment UI with a mandatory reason and ConfirmModal confirmation.
 *
 * Pattern strictly follows MCDocumentVerificationModal.js:
 *   - modal-overlay / modal-container / modal-header / modal-content tokens
 *   - useIsMobile + MobileVerificationTabs for mobile layout
 *   - w-2/3 (document) / w-1/3 (form) split-screen proportions
 *   - btn-success / btn-danger / btn-admin-outline buttons
 *   - Sticky right-panel footer with action buttons
 *   - form-group wrappers, form-label, input-admin, textarea-admin
 *   - LoadingSpinner for all loading states
 *   - ConfirmModal for amendment confirmation
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, XCircle, CheckCircle, AlertTriangle, User,
  Download, ZoomIn, ZoomOut, RotateCw, RefreshCw,
  Plane, Upload,
} from 'lucide-react';
import { staffAPI } from '../services/api';
import toast from '../utils/toast';
import Portal from './UI/Portal';
import LoadingSpinner from './UI/LoadingSpinner';
import PdfViewer from './UI/PdfViewer';
import MobileVerificationTabs from './UI/MobileVerificationTabs';
import VerificationPanelHeader from './UI/VerificationPanelHeader';
import ExtractAgainButton from './UI/ExtractAgainButton';
import { InfoBadge } from './UI/InfoBadge';
import { StatusBadge } from './UI/StatusBadge';
import { useIsMobile } from '../hooks/useIsMobile';
import { useImageViewer } from '../hooks/useImageViewer';
import { formatDate, parseLocalDate, toLocalISODate } from '../utils/dateUtils';
import { formatLeaveDays } from '../utils/numberUtils';
import { isHeicBlob, convertHeicBlobToJpeg, normalizeHeicFileForUpload } from '../utils/imageUtils';
import DatePickerField from './UI/DatePickerField';
import DatalistCombobox from './UI/DatalistCombobox';
import {
  AIRPORT_SUGGESTIONS,
  FLIGHT_TRIP_TYPE_LABELS,
  isOneWayOutboundTrip,
} from '../utils/flightTicketUtils';

// ─── Document preview panel ───────────────────────────────────────────────────
const DocumentPreviewPanel = ({
  documentApprovalId,
  hidePanelHeader = false,
  previewNonce = 0,
  canUpload = false,
  uploadBusy = false,
  onUploadClick,
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const blobRef = useRef(null);
  const viewer = useImageViewer();

  useEffect(() => {
    if (!documentApprovalId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await staffAPI.downloadDocumentFile(documentApprovalId);
        const mime = res.headers['content-type'] || 'application/octet-stream';
        let blob = new Blob([res.data], { type: mime });
        let effectiveMime = mime;

        if (isHeicBlob(blob) || mime === 'image/heic' || mime === 'image/heif') {
          blob = await convertHeicBlobToJpeg(blob);
          effectiveMime = 'image/jpeg';
        }

        const url = URL.createObjectURL(blob);
        if (!cancelled) {
          if (blobRef.current) URL.revokeObjectURL(blobRef.current);
          blobRef.current = url;
          setBlobUrl(url);
          setContentType(effectiveMime);
        }
      } catch (e) {
        console.error('FlightTicketVerificationModal: document download failed:', e?.response?.status, e?.message);
        if (!cancelled) {
          const status = e?.response?.status;
          if (status === 403) {
            setError('You do not have permission to view this document. Please contact your administrator.');
          } else if (status === 404) {
            setError('Document file not found on the server.');
          } else {
            setError('Could not load document preview.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [documentApprovalId, previewNonce]);

  useEffect(() => {
    return () => { if (blobRef.current) URL.revokeObjectURL(blobRef.current); };
  }, []);

  const isPdf = contentType?.includes('pdf') || false;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Desktop only — mobile tabs already label the Ticket surface */}
      {!hidePanelHeader && (
        <VerificationPanelHeader
          title="Flight Ticket"
          trailing={canUpload && documentApprovalId ? (
            <button
              type="button"
              onClick={onUploadClick}
              disabled={uploadBusy}
              className="btn btn-sm btn-admin-outline inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <Upload className="w-3.5 h-3.5" aria-hidden="true" />
              Replace
            </button>
          ) : null}
        />
      )}
      {hidePanelHeader && canUpload && documentApprovalId && (
        <div className="px-3 py-2 border-b border-gray-200 bg-white flex justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onUploadClick}
            disabled={uploadBusy}
            className="btn btn-sm btn-admin-outline inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            <Upload className="w-3.5 h-3.5" aria-hidden="true" />
            Replace
          </button>
        </div>
      )}

      {/* Preview area */}
      <div className="flex-1 overflow-hidden bg-gray-100 flex items-center justify-center relative min-h-0">
        {loading && (
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <LoadingSpinner size="lg" />
            <span className="text-sm">Loading document…</span>
          </div>
        )}

        {error && !loading && (
          <div className="text-center text-gray-600 p-6 max-w-sm">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
            <div className="text-sm font-medium text-gray-800">{error}</div>
            {canUpload && (
              <>
                <p className="text-xs text-gray-500 mt-1.5">
                  Re-attach the booking copy the worker sent you to continue verification.
                </p>
                <button
                  type="button"
                  onClick={onUploadClick}
                  disabled={uploadBusy}
                  className="btn btn-sm btn-admin-primary mt-4 inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  <Upload className="w-3.5 h-3.5" aria-hidden="true" />
                  Attach ticket
                </button>
              </>
            )}
          </div>
        )}

        {!documentApprovalId && !loading && !error && (
          <div className="text-center text-gray-600 p-6 max-w-sm">
            <Plane className="w-10 h-10 mx-auto mb-2 text-gray-400" />
            <div className="text-sm font-medium text-gray-800">No ticket on file</div>
            <p className="text-xs text-gray-500 mt-1.5">
              Upload the booking copy the worker sent you — WhatsApp photo or PDF. Extracted details appear on the right, then you verify.
            </p>
            {canUpload && (
              <button
                type="button"
                onClick={onUploadClick}
                disabled={uploadBusy}
                className="btn btn-sm btn-admin-primary mt-4 inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                <Upload className="w-3.5 h-3.5" aria-hidden="true" />
                Upload ticket
              </button>
            )}
          </div>
        )}

        {blobUrl && !loading && !error && (
          isPdf ? (
            <div className="relative w-full h-full border rounded-lg bg-white overflow-hidden">
              <PdfViewer
                src={blobUrl}
                title="Flight Ticket"
                fileName="flight_ticket.pdf"
              />
            </div>
          ) : (
            <div
              ref={viewer.containerRef}
              className="relative w-full h-full overflow-hidden bg-white border rounded-lg flex items-center justify-center"
              {...viewer.panHandlers}
              style={viewer.containerStyle}
            >
              <img
                src={blobUrl}
                alt="Flight Ticket"
                className="select-none pointer-events-none rounded-lg shadow-md"
                draggable={false}
                style={{
                  transform: viewer.imageTransform,
                  transformOrigin: 'center center',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transition: 'transform 0.2s ease',
                }}
              />
              {/* Floating zoom controls — image viewer only */}
              <div
                className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 bg-white/95 border border-gray-200 rounded-full shadow-md p-1"
                onMouseDown={e => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={viewer.zoomOut}
                  className="icon-button-admin"
                  title="Zoom out"
                  aria-label="Zoom out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={viewer.zoomIn}
                  className="icon-button-admin"
                  title="Zoom in"
                  aria-label="Zoom in"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={viewer.rotate}
                  className="icon-button-admin"
                  title="Rotate clockwise"
                  aria-label="Rotate clockwise"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={viewer.reset}
                  className="icon-button-admin"
                  title="Fit to panel"
                  aria-label="Fit to panel"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <div className="w-px h-6 bg-gray-200 mx-0.5" />
                <a
                  href={blobUrl}
                  download="flight_ticket"
                  className="icon-button-admin"
                  title="Download"
                  aria-label="Download flight ticket"
                >
                  <Download className="w-4 h-4" />
                </a>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FlightTicketVerificationModal = ({
  isOpen,
  onClose,
  documentApprovalId,
  leaveApplication,
  onVerificationComplete,
}) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('document');
  const leave = leaveApplication;
  const isAlreadyVerified = leave?.flight_ticket_verification?.status === 'approved';
  const isUrgentLeave = (leave?.leave_type?.leave_type_name || '').toLowerCase().includes('urgent');

  // Confirmed flight fields (editable by HR before approval)
  const [depDate, setDepDate] = useState(leave?.flight_departure_date || '');
  const [retDate, setRetDate] = useState(leave?.flight_return_date || '');
  const [bookingRef, setBookingRef] = useState(leave?.flight_booking_reference || '');
  const [depAirport, setDepAirport] = useState(leave?.flight_departure_airport || '');
  const [arrAirport, setArrAirport] = useState(leave?.flight_arrival_airport || '');
  const [depFlightNum, setDepFlightNum] = useState(leave?.flight_departure_flight_number || '');
  const [arrFlightNum, setArrFlightNum] = useState(leave?.flight_arrival_flight_number || '');

  // Reject form
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');


  const [submitting, setSubmitting] = useState(false);
  const [activeDaId, setActiveDaId] = useState(documentApprovalId || null);
  const [previewNonce, setPreviewNonce] = useState(0);
  const [uploadingTicket, setUploadingTicket] = useState(false);
  const fileInputRef = useRef(null);

  // OCR state
  const [flightOcrData, setFlightOcrData] = useState(null);
  const [loadingOcrData, setLoadingOcrData] = useState(false);
  const [triggeringOcr, setTriggeringOcr] = useState(false);
  const [ocrFailed, setOcrFailed] = useState(false);
  // Track whether we've already auto-filled from OCR so we don't overwrite
  // any corrections the HR officer made after the initial pre-fill.
  const ocrPrefilledRef = useRef(false);
  const autoTriggeredRef = useRef(false);
  const isOneWayOutbound = isOneWayOutboundTrip(
    flightOcrData?.ocr_trip_type,
  );
  const requiresReturn = !isUrgentLeave && !isOneWayOutbound;

  useEffect(() => {
    setActiveDaId(documentApprovalId || null);
  }, [documentApprovalId]);

  // Sync confirmed fields when leave prop changes (e.g. parent refetches)
  useEffect(() => {
    if (leave) {
      setDepDate(leave.flight_departure_date || '');
      setRetDate(isUrgentLeave ? '' : (leave.flight_return_date || ''));
      setBookingRef(leave.flight_booking_reference || '');
      setDepAirport(leave.flight_departure_airport || '');
      setArrAirport(leave.flight_arrival_airport || '');
      setDepFlightNum(leave.flight_departure_flight_number || '');
      setArrFlightNum(isUrgentLeave ? '' : (leave.flight_arrival_flight_number || ''));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leave?.id]);

  // OCR is immutable ticket evidence. Once it confirms there is no return leg,
  // remove stale worker-entered return values before HR can approve them.
  useEffect(() => {
    if (isUrgentLeave || isOneWayOutbound) {
      setRetDate('');
      setArrFlightNum('');
    }
  }, [isUrgentLeave, isOneWayOutbound]);

  // Initial OCR data fetch when the modal opens
  useEffect(() => {
    if (!isOpen || !activeDaId) return;
    let cancelled = false;
    ocrPrefilledRef.current = false;
    autoTriggeredRef.current = false;
    setOcrFailed(false);

    const load = async () => {
      setLoadingOcrData(true);
      try {
        const res = await staffAPI.getFlightTicketVerificationData(activeDaId);
        if (!cancelled) setFlightOcrData(res.data);
      } catch {
        // Non-critical — OCR is best-effort; HR can still verify manually
        if (!cancelled) setFlightOcrData(null);
      } finally {
        if (!cancelled) setLoadingOcrData(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [isOpen, activeDaId]);

  const handleTriggerOcr = useCallback(async ({ silent = false, file = null } = {}) => {
    if (!activeDaId || triggeringOcr || isAlreadyVerified) return;
    setTriggeringOcr(true);
    setOcrFailed(false);
    const loadingToast = silent ? null : toast.loading('Reading flight ticket…');

    try {
      const staffId = leave?.staff_code || leave?.leave_staff_id_id || '';
      const resp = await staffAPI.sendFlightTicketToN8N(
        activeDaId,
        file,
        staffId,
        leave?.id,
      );
      const payload = resp?.data || {};

      const applyCompleted = (data) => {
        setFlightOcrData(data);
        if (loadingToast) toast.dismiss(loadingToast);
        toast.success('Ticket details extracted');
      };

      const verify = await staffAPI.getFlightTicketVerificationData(activeDaId);
      if (verify.data?.status === 'ocr_completed') {
        applyCompleted(verify.data);
        return;
      }

      // n8n accepted the file but persist was empty — webhook may still arrive
      if (payload.success && !payload.ocr_persisted) {
        for (let attempt = 0; attempt < 6; attempt++) {
          await new Promise((r) => setTimeout(r, 5000));
          const polled = await staffAPI.getFlightTicketVerificationData(activeDaId);
          if (polled.data?.status === 'ocr_completed') {
            applyCompleted(polled.data);
            return;
          }
        }
      }

      if (loadingToast) toast.dismiss(loadingToast);
      toast.warning(
        payload.message
          || 'Could not extract ticket details. You can retry or continue with the worker-confirmed values.',
        { duration: 6000 },
      );
      setOcrFailed(true);
    } catch (error) {
      if (loadingToast) toast.dismiss(loadingToast);
      toast.error(
        error?.response?.data?.message
          || error?.response?.data?.error
          || 'Failed to extract ticket details. Please try again.',
      );
      setOcrFailed(true);
    } finally {
      setTriggeringOcr(false);
    }
  }, [activeDaId, triggeringOcr, isAlreadyVerified, leave]);

  const handleHrFileChange = async (event) => {
    const raw = event.target.files?.[0];
    event.target.value = '';
    if (!raw || !leave?.id) {
      if (!leave?.id) toast.error('Leave application is missing. Close and reopen this ticket.');
      return;
    }
    if (raw.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5 MB limit');
      return;
    }

    setUploadingTicket(true);
    const loadingToast = toast.loading('Attaching flight ticket…');
    try {
      const file = await normalizeHeicFileForUpload(raw);
      const uploadResp = await staffAPI.uploadFlightTicket(leave.id, file);
      const newDaId = uploadResp?.data?.document_approval_id;
      if (newDaId) setActiveDaId(newDaId);
      setPreviewNonce((n) => n + 1);
      ocrPrefilledRef.current = false;
      autoTriggeredRef.current = true;
      setFlightOcrData(null);
      toast.dismiss(loadingToast);
      toast.success('Ticket attached. Reading details…');

      // Send the browser file so OCR does not depend on this machine's MEDIA_ROOT.
      setTriggeringOcr(true);
      setOcrFailed(false);
      try {
        const staffId = leave?.staff_code || leave?.leave_staff_id_id || '';
        const daId = newDaId || activeDaId;
        await staffAPI.sendFlightTicketToN8N(daId, file, staffId, leave.id);
        const verify = await staffAPI.getFlightTicketVerificationData(daId);
        setFlightOcrData(verify.data);
        if (verify.data?.status === 'ocr_completed') {
          toast.success('Ticket details extracted');
        } else {
          setOcrFailed(true);
          toast.warning('Ticket saved. Could not extract details — retry or verify manually.');
        }
      } catch {
        setOcrFailed(true);
        toast.warning('Ticket saved. Could not extract details — retry or verify manually.');
      } finally {
        setTriggeringOcr(false);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error?.response?.data?.error || 'Failed to attach ticket.');
    } finally {
      setUploadingTicket(false);
    }
  };

  // HR opened a ticket with no extract — actually run OCR from the stored file.
  // The old "processing" banner was a sentinel (no FlightTicketOCRData row),
  // not a live n8n job. Auto-fire once per open so HR does not wait on a lie.
  useEffect(() => {
    if (!isOpen || !activeDaId || isAlreadyVerified) return;
    if (loadingOcrData || triggeringOcr || uploadingTicket) return;
    if (flightOcrData?.status === 'ocr_completed') return;
    if (autoTriggeredRef.current) return;
    autoTriggeredRef.current = true;
    handleTriggerOcr({ silent: true });
  }, [
    isOpen,
    activeDaId,
    isAlreadyVerified,
    loadingOcrData,
    triggeringOcr,
    uploadingTicket,
    flightOcrData?.status,
    handleTriggerOcr,
  ]);

  // Auto-pre-fill confirmed fields from OCR data the first time extraction
  // completes.  Only fills fields that are still empty so we never overwrite
  // data the worker already provided or the HR officer has already corrected.
  useEffect(() => {
    if (flightOcrData?.status !== 'ocr_completed') return;
    if (ocrPrefilledRef.current) return;
    ocrPrefilledRef.current = true;

    if (!bookingRef && flightOcrData.ocr_booking_reference)
      setBookingRef(flightOcrData.ocr_booking_reference);
    if (!depAirport && flightOcrData.ocr_departure_airport)
      setDepAirport(flightOcrData.ocr_departure_airport);
    if (!arrAirport && flightOcrData.ocr_arrival_airport)
      setArrAirport(flightOcrData.ocr_arrival_airport);
    if (!depDate && flightOcrData.ocr_departure_date)
      setDepDate(flightOcrData.ocr_departure_date);
    if (requiresReturn && !retDate && flightOcrData.ocr_return_date)
      setRetDate(flightOcrData.ocr_return_date);
    if (!depFlightNum && flightOcrData.ocr_departure_flight_number)
      setDepFlightNum(flightOcrData.ocr_departure_flight_number);
    if (requiresReturn && !arrFlightNum && flightOcrData.ocr_arrival_flight_number)
      setArrFlightNum(flightOcrData.ocr_arrival_flight_number);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightOcrData?.status]);

  // Date mismatch flags (informational for HR)
  const depMismatch = depDate && leave?.leave_start_date && depDate !== leave.leave_start_date;
  const retMismatch = requiresReturn && retDate && leave?.leave_end_date && retDate !== leave.leave_end_date;

  const handleApprove = async () => {
    if (!activeDaId) return;
    if (!depDate) {
      toast.error('Departure date is required');
      return;
    }
    if (requiresReturn && !retDate) {
      toast.error('Return date is required for a return ticket');
      return;
    }
    setSubmitting(true);
    try {
      await staffAPI.approveDocument(activeDaId, {
        flight_booking_reference: bookingRef || '',
        flight_departure_airport: depAirport || '',
        flight_arrival_airport: arrAirport || '',
        flight_departure_date: depDate || null,
        flight_return_date: requiresReturn ? (retDate || null) : null,
        flight_departure_flight_number: depFlightNum || '',
        flight_arrival_flight_number: requiresReturn ? (arrFlightNum || '') : '',
      });
      toast.success('Flight ticket verified successfully.');
      onVerificationComplete?.('approved');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to approve. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('A rejection reason is required');
      return;
    }
    setSubmitting(true);
    try {
      await staffAPI.rejectDocument(activeDaId, { reason: rejectionReason });
      toast.success('Flight ticket rejected. Worker will be notified to re-submit.');
      onVerificationComplete?.('rejected');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to reject. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay">
        <div className={`modal-container max-w-7xl ${isMobile ? 'h-[calc(100dvh-1rem)]' : 'h-[92vh] max-h-[96vh]'} flex flex-col overflow-hidden`}>

          {/* ── Header — DS: title + StatusBadge; HR context subtitle; close alone */}
          <div className="modal-header">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <h3 className="modal-title truncate min-w-0">Flight Ticket Verification</h3>
                {isAlreadyVerified ? (
                  <div className="flex-shrink-0">
                    <StatusBadge status="approved" label="Verified" showIcon />
                  </div>
                ) : null}
              </div>
              <p className="text-sm text-gray-500 mt-0.5 truncate">
                {leave?.leave_staff_id?.get_full_name || leave?.staff_name || leave?.employee_name || 'Employee'}
                {(leave?.staff_code || leave?.leave_staff_id_id) && (
                  <>
                    <span className="mx-1.5 text-gray-400">·</span>
                    {leave?.staff_code || leave?.leave_staff_id_id}
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="modal-close-admin flex-shrink-0"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* ── Content (split-screen) ─────────────────────────────────────── */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
            className="hidden"
            onChange={handleHrFileChange}
          />
          <div className="modal-content !overflow-hidden !p-0 flex flex-col">
            <div className={`flex-1 flex ${isMobile ? 'flex-col' : 'flex-row'} overflow-hidden min-h-0`}>

              {/* Mobile tab bar */}
              {isMobile && (
                <MobileVerificationTabs
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  documentLabel="Ticket"
                  formLabel="Verification"
                  isAdmin={true}
                />
              )}

              {/* Left panel — Document preview (w-2/3 on desktop) */}
              <div className={`${isMobile ? (activeTab === 'document' ? 'flex-1' : 'hidden') : 'w-2/3'} ${!isMobile ? 'border-r' : ''} border-gray-200 flex flex-col min-h-0`}>
                <DocumentPreviewPanel
                  documentApprovalId={activeDaId}
                  previewNonce={previewNonce}
                  hidePanelHeader={isMobile}
                  canUpload={!isAlreadyVerified}
                  uploadBusy={uploadingTicket || triggeringOcr}
                  onUploadClick={() => fileInputRef.current?.click()}
                />
              </div>

              {/* Right panel — Extracted data + actions (w-1/3 on desktop) */}
              <div className={`${isMobile ? (activeTab === 'form' ? 'flex-1' : 'hidden') : 'w-1/3'} flex flex-col min-h-0 min-w-0 overflow-hidden`}>

                {/* Desktop only — mobile tabs already label Verification; trip-type chips stay here */}
                {!isMobile && (
                  <VerificationPanelHeader
                    title="Flight Details"
                    trailing={isAlreadyVerified ? (
                      <InfoBadge color="green" label="Verified" />
                    ) : (
                      <>
                        {flightOcrData?.status === 'ocr_completed' && (() => {
                          const extractedType = flightOcrData.ocr_trip_type || 'unknown';
                          const tripLabel = FLIGHT_TRIP_TYPE_LABELS[extractedType] || 'Unknown';
                          const expectsReturn = (
                            !isUrgentLeave
                            && !isOneWayOutboundTrip(extractedType)
                          );
                          const isReturn = ['round_trip', 'return'].includes(extractedType);
                          const mismatch = expectsReturn ? !isReturn : isReturn;
                          return <InfoBadge color={mismatch ? 'amber' : 'blue'} label={tripLabel} />;
                        })()}
                        {(loadingOcrData || triggeringOcr) && <LoadingSpinner size="sm" />}
                      </>
                    )}
                  />
                )}

                {/* Scrollable form area */}
                <div
                  className="flex-1 p-4 overflow-y-auto overflow-x-hidden overscroll-y-contain min-h-0 space-y-4"
                  style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
                >
                  {!activeDaId && !uploadingTicket && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                      <div className="flex items-start gap-2">
                        <Upload className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-blue-800">Upload on behalf of the worker</div>
                          <p className="text-xs text-blue-700 mt-0.5">
                            Use this when they sent the booking copy by WhatsApp or email. Attach it on the ticket panel, then verify here.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* In-flight OCR — only while a real trigger request is running */}
                  {triggeringOcr && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      <div className="text-xs text-amber-800 font-medium">
                        Reading ticket — extracted details will appear shortly
                      </div>
                    </div>
                  )}

                  {/* Honest failure — scan never completed; HR can retry or continue */}
                  {!triggeringOcr && ocrFailed && flightOcrData?.status !== 'ocr_completed' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-amber-800">Ticket details not extracted</div>
                          <p className="text-xs text-amber-700 mt-0.5">
                            The scan did not finish. Extract now, or continue with the worker-confirmed details below.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <ExtractAgainButton
                          onClick={() => handleTriggerOcr()}
                          busy={triggeringOcr}
                        />
                      </div>
                    </div>
                  )}

                  {!isAlreadyVerified && flightOcrData?.status === 'ocr_completed' && !ocrFailed ? (
                    <div className="flex justify-end">
                      <ExtractAgainButton
                        onClick={() => handleTriggerOcr()}
                        busy={triggeringOcr}
                      />
                    </div>
                  ) : null}

                  {/* Immutable OCR evidence — kept separate from worker/HR-confirmed fields */}
                  {flightOcrData?.status === 'ocr_completed' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="text-xs font-semibold text-gray-700">Ticket evidence</div>
                        <div className="ml-auto">
                          <InfoBadge color="gray" label="OCR extracted" />
                        </div>
                      </div>
                      <dl className="space-y-1 text-xs">
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-gray-500">Passenger</dt>
                          <dd className="font-medium text-gray-800 truncate uppercase">
                            {flightOcrData.ocr_passenger_name || '—'}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-gray-500">Trip type</dt>
                          <dd className="font-medium text-gray-800">
                            {FLIGHT_TRIP_TYPE_LABELS[flightOcrData.ocr_trip_type] || 'Unknown'}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-gray-500">Ticket dates</dt>
                          <dd className="font-medium text-gray-800 text-right">
                            {flightOcrData.ocr_departure_date ? formatDate(flightOcrData.ocr_departure_date) : '—'}
                            {flightOcrData.ocr_return_date ? ` → ${formatDate(flightOcrData.ocr_return_date)}` : ''}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}

                  {/* Date mismatch notice */}
                  {(depMismatch || retMismatch) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 min-w-0">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-amber-800">Date mismatch detected</div>
                        {depMismatch && (
                          <div className="text-xs text-amber-700 mt-0.5">
                            Departure: ticket <strong>{formatDate(depDate)}</strong> vs approved <strong>{formatDate(leave.leave_start_date)}</strong>
                          </div>
                        )}
                        {retMismatch && (
                          <div className="text-xs text-amber-700 mt-0.5">
                            Return: ticket <strong>{formatDate(retDate)}</strong> vs approved <strong>{formatDate(leave.leave_end_date)}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Approved leave period reference */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <div className="text-xs font-semibold text-blue-800 mb-1">Approved Leave Period</div>
                    <div className="text-sm text-blue-700 font-medium">
                      {formatDate(leave?.leave_start_date)} → {formatDate(leave?.leave_end_date)}
                    </div>
                    <div className="text-xs text-blue-600 mt-0.5">
                      {formatLeaveDays(leave?.leave_total_days)} day{Number(leave?.leave_total_days) !== 1 ? 's' : ''}
                      {leave?.leave_consumption_proposal?.items?.length > 0 && (
                        <span className="ml-2">
                          ({leave.leave_consumption_proposal.items.map(i => `${formatLeaveDays(i.days)}d ${i.leave_type}`).join(' + ')})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Confirmed flight details */}
                  <div className="bg-white rounded-xl shadow-sm border p-4">
                    {/* DS Compact Page Header / leading-icon title row:
                        icon anchors title + badge; helper is a sibling under the
                        title (full text-column width), never bundled beside the badge. */}
                    <div className="flex items-start gap-2 mb-3">
                      <Plane className="w-4 h-4 text-blue-600 flex-shrink-0 mt-1" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2.5 min-w-0">
                          <h5 className="text-base font-semibold text-gray-900 min-w-0">
                            Flight Details for Verification
                          </h5>
                          <InfoBadge
                            className="flex-shrink-0"
                            color={isAlreadyVerified ? 'green' : 'blue'}
                            label={isAlreadyVerified ? 'HR verified' : 'Worker confirmed'}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {isAlreadyVerified
                            ? 'These are the final values verified by HR.'
                            : 'Prefilled from worker-confirmed values. Your edits become HR-verified on approval.'}
                        </p>
                      </div>
                    </div>

                    {isOneWayOutbound && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-3">
                        <div className="flex items-start gap-2">
                          <Plane className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <p className="text-xs text-blue-800">
                            <span className="font-semibold">One-way outbound ticket.</span>
                            {' '}Return details are not applicable and will be saved blank. The approved leave end date remains unchanged.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      <div className={`grid grid-cols-1 ${requiresReturn ? 'sm:grid-cols-2' : ''} gap-3`}>
                        <div className="form-group">
                          <label className="form-label">Departure Date</label>
                          {isAlreadyVerified ? (
                            <p className="text-sm font-medium text-gray-900">{depDate ? formatDate(depDate) : '—'}</p>
                          ) : (
                            <DatePickerField
                              theme="admin"
                              selected={parseLocalDate(depDate)}
                              onChange={(date) => setDepDate(date ? toLocalISODate(date) : '')}
                              placeholderText="Select date"
                            />
                          )}
                        </div>
                        {requiresReturn && (
                          <div className="form-group">
                            <label className="form-label">Return Date</label>
                            {isAlreadyVerified ? (
                              <p className="text-sm font-medium text-gray-900">{retDate ? formatDate(retDate) : '—'}</p>
                            ) : (
                              <DatePickerField
                                theme="admin"
                                selected={parseLocalDate(retDate)}
                                onChange={(date) => setRetDate(date ? toLocalISODate(date) : '')}
                                placeholderText="Select date"
                              />
                            )}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="form-group">
                          <label className="form-label">Departure Airport</label>
                          <DatalistCombobox
                            value={depAirport}
                            onChange={setDepAirport}
                            suggestions={AIRPORT_SUGGESTIONS}
                            placeholder="e.g. Singapore (SIN)"
                            disabled={isAlreadyVerified}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Arrival Airport</label>
                          <DatalistCombobox
                            value={arrAirport}
                            onChange={setArrAirport}
                            suggestions={AIRPORT_SUGGESTIONS}
                            placeholder="e.g. Tiruchirappalli (TRZ)"
                            disabled={isAlreadyVerified}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Booking Reference / PNR</label>
                        <input
                          type="text"
                          placeholder="e.g. ABC123"
                          value={bookingRef}
                          onChange={e => setBookingRef(e.target.value)}
                          className="input-admin font-mono"
                          readOnly={isAlreadyVerified}
                        />
                      </div>

                      <div className={`grid grid-cols-1 ${requiresReturn ? 'sm:grid-cols-2' : ''} gap-3`}>
                        <div className="form-group">
                          <label className="form-label">Departure Flight No.</label>
                          {isAlreadyVerified ? (
                            <p className="text-sm font-medium text-gray-900">{depFlightNum || '—'}</p>
                          ) : (
                            <input
                              type="text"
                              placeholder="e.g. TR 123"
                              value={depFlightNum}
                              onChange={e => setDepFlightNum(e.target.value)}
                              className="input-admin"
                            />
                          )}
                        </div>
                        {requiresReturn && (
                          <div className="form-group">
                            <label className="form-label">Return Flight No.</label>
                            {isAlreadyVerified ? (
                              <p className="text-sm font-medium text-gray-900">{arrFlightNum || '—'}</p>
                            ) : (
                              <input
                                type="text"
                                placeholder="e.g. TR 456"
                                value={arrFlightNum}
                                onChange={e => setArrFlightNum(e.target.value)}
                                className="input-admin"
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Reject form — hidden when already verified */}
                  {!isAlreadyVerified && showRejectForm && (
                    <div className="border border-red-200 bg-red-50 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-semibold text-red-800">Rejection Reason <span className="text-red-500">*</span></span>
                      </div>
                      <div className="form-group">
                        <textarea
                          rows={3}
                          placeholder="Enter the reason for rejecting this flight ticket…"
                          value={rejectionReason}
                          onChange={e => setRejectionReason(e.target.value)}
                          className="textarea-admin"
                        />
                      </div>
                      <div className="flex gap-2 btn-row-spread">
                        <button
                          type="button"
                          onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                          className="btn btn-admin-outline"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleReject}
                          disabled={submitting || !rejectionReason.trim()}
                          className="btn btn-danger"
                        >
                          {submitting ? 'Rejecting…' : 'Confirm Rejection'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sticky action footer — only when NOT yet verified */}
                {isAlreadyVerified ? (
                  <div className="sticky bottom-0 bg-green-50 border-t border-green-200 p-4 flex-shrink-0 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-700">Flight Ticket Verified</span>
                  </div>
                ) : !showRejectForm && (
                  <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex-shrink-0 flex gap-3 btn-row-spread">
                    <button
                      type="button"
                      onClick={() => setShowRejectForm(true)}
                      disabled={submitting || !activeDaId || uploadingTicket}
                      className="btn btn-danger"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={
                        submitting
                        || !activeDaId
                        || uploadingTicket
                        || !depDate
                        || (requiresReturn && !retDate)
                      }
                      className="btn btn-success"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {submitting ? 'Verifying…' : 'Verify Ticket'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </Portal>
  );
};

export default FlightTicketVerificationModal;
